import React, { Component } from 'react';
import PropTypes from 'prop-types';
import FieldsContainer from '../fields/FieldsContainer';
import DropdownField from '../fields/DropdownField';
import ClientField from '../fields/ClientField';
import TextField from '../fields/TextField';
import DateTimeField from '../fields/DateTimeField';
import TextArrayField from '../fields/TextArrayField';
import { buySellOptions } from '../../utility/commonFieldOptions';
import { toUI } from '../../utility/mapStaticData';
import { extractNumber, addCommasToNum } from '../../utility/fieldFormatters';
import { validateAllFields, clearErrorsAllFields, FIELD_TYPE, isInExecutedState, isClientRateMandatory } from '../../utility/validators';

class OrderDetailComponent extends Component {
  constructor(props) {
    super(props);
    this.fields = {};
  }

  static get propTypes() {
    return {
      record: PropTypes.object.isRequired,
      editing: PropTypes.bool,
      clipboardDate: PropTypes.any,
      recentlyUsed: PropTypes.object,
      staticData: PropTypes.object,
      onUpdateProperty: PropTypes.func.isRequired,
      onSetCurrentTime: PropTypes.func,
      onFetchClients: PropTypes.func,
      onCopyDate: PropTypes.func,
      onFetchNotional: PropTypes.func
    };
  }

  validate() {
    return validateAllFields(this.fields);
  }

  clearErrors() {
    clearErrorsAllFields(this.fields);
  }

  render() {
    const { record, editing, staticData, onUpdateProperty,
      onFetchClients, onSetCurrentTime, recentlyUsed,
      clipboardDate, onCopyDate, onFetchNotional } = this.props;

    return (
      <div className="OrderDetail">
        <FieldsContainer>
          <div className="FieldsContainer-group">
            <div className="RecordDetail-id">
              {record.OrderNumber ? `Order ${record.OrderNumber}` : 'New Order'}
            </div>

            <DropdownField
              name="business-group"
              label="Business Group"
              editing={editing}
              required
              ref={(instance) => { this.fields.businessGroup = instance; }}
              value={record.BusinessGroup}
              possibleValues={staticData.businessGroups}
              mapFn={toUI.businessGroup}
              onChange={updatedValue => onUpdateProperty('BusinessGroup', updatedValue)}
              toolTip="Sales desk where Order/RFQ is registered"
            />
          </div>

          <TextField
            name="instrument-description"
            label="Instrument Description"
            ref={(instance) => { this.fields.instrumentDescription = instance; }}
            editing={editing}
            value={record.InstrumentDescription}
            multiLine
            onChange={updatedValue => onUpdateProperty('InstrumentDescription', updatedValue)}
          />

          <ClientField
            name="client"
            editing={editing}
            recentlyUsed={recentlyUsed.Client}
            required
            ref={(instance) => { this.fields.client = instance; }}
            onFetchClients={(input, type) => onFetchClients(input, type)}
            value={record.Client}
            onChange={updatedValue => onUpdateProperty('Client', updatedValue)}
            toolTip="Name of client (Start search with ^ to match from the start of Client names)"
          />
          <div className="FieldsContainer-group">
            <TextField
              name="client-contact"
              label="Client Contact"
              ref={(instance) => { this.fields.clientContact = instance; }}
              editing={editing}
              value={record.ClientContact}
              onChange={updatedValue => onUpdateProperty('ClientContact', updatedValue)}
              toolTip="Name of Salesperson/trader client side"
            />
            <DropdownField
              name="order-type"
              label="Order Type"
              ref={(instance) => { this.fields.ordertype = instance; }}
              editing={editing}
              required
              value={record.OrderType}
              possibleValues={staticData.orderTypes}
              mapFn={toUI.orderType}
              onChange={updatedValue => onUpdateProperty('OrderType', updatedValue)}
              validationMessage={'Please choose a Order Type'}
              toolTip="Contingency based order"
            />
          </div>

          <DateTimeField
            name="good-till"
            label="Good-till-Date"
            editing={editing}
            required={false}
            onChange={updatedValue => onUpdateProperty('GoodTillDate', updatedValue)}
            timeZones={staticData.timeZones}
            value={record.GoodTillDate}
            onSetCurrentTime={timeZoneName => onSetCurrentTime('GoodTillDate', timeZoneName)}
            clipboardDate={clipboardDate}
            onCopy={onCopyDate}
            toolTip="Date until the order expires"
          />

          <div className="FieldsContainer-group">
            <DropdownField
              name="buy-sell"
              label="Client Buy/Sell"
              ref={(instance) => { this.fields.buySell = instance; }}
              required
              editing={editing}
              value={record.BuySell}
              possibleValues={buySellOptions}
              mapFn={toUI.buySell}
              onChange={updatedValue => onUpdateProperty('BuySell', updatedValue)}
              validationMessage={'Please select Buy/Sell Indicator'}
            />
            <TextField
              name="client-rate"
              label="Client Price"
              required={isClientRateMandatory(record)}
              ref={(instance) => { this.fields.clientRate = instance; }}
              inputType={FIELD_TYPE.COMMA_NUMERIC}
              editing={editing}
              value={addCommasToNum(record.ClientRate, 6)}
              onChange={updatedValue => onUpdateProperty('ClientRate', extractNumber(updatedValue))}
              validationMessage={'Please specify a Client Price'}
              toolTip="Price quoted to client including sales margin"
              onLoseFocus={onFetchNotional}
            />
            <TextField
              name="trader-price"
              label="Trader Price"
              required={isInExecutedState(record)}
              ref={(instance) => { this.fields.traderPrice = instance; }}
              inputType={FIELD_TYPE.COMMA_NUMERIC_GRATER_THAN_ZERO}
              editing={editing}
              value={addCommasToNum(record.TraderPrice, 6)}
              onChange={updatedValue => onUpdateProperty('TraderPrice', extractNumber(updatedValue))}
              validationMessage="Please enter a price greater than zero"
              toolTip="Interbank executed price"
            />
          </div>

          <div className="FieldsContainer-group">
            <TextField
              name="quantity"
              label="Quantity"
              required
              ref={(instance) => { this.fields.quantity = instance; }}
              editing={editing}
              value={addCommasToNum(record.Quantity)}
              validationMessage={'Please specify a Quantity'}
              inputType={FIELD_TYPE.COMMA_NUMERIC}
              onChange={updatedValue => onUpdateProperty('Quantity', extractNumber(updatedValue))}
              toolTip="Notional amount, amount bought/sold, commodity unit"
              onLoseFocus={onFetchNotional}
            />
            <TextField
              name="total-filled"
              label="Total Filled"
              ref={(instance) => { this.fields.totalFilled = instance; }}
              editing={editing}
              value={addCommasToNum(record.TotalFilled)}
              validationMessage={'Please enter a valid number'}
              inputType={FIELD_TYPE.COMMA_NUMERIC}
              onChange={updatedValue => onUpdateProperty('TotalFilled', extractNumber(updatedValue))}
              toolTip="Partly filled orders"
            />
            <DropdownField
              name="unit"
              label="Unit"
              editing={editing}
              value={record.UnitType}
              possibleValues={staticData.unitTypes}
              mapFn={toUI.unitType}
              onChange={(updatedValue) => { onUpdateProperty('UnitType', updatedValue); onFetchNotional(null, updatedValue); }}
              toolTip="Unit description"
            />
          </div>

          <div className="FieldsContainer-group">
            <TextField
              name="notional"
              label="Notional"
              ref={(instance) => { this.fields.notional = instance; }}
              editing={false}
              value={addCommasToNum(record.Notional, 2)}
              inputType={FIELD_TYPE.COMMA_NUMERIC}
              toolTip="Notional"
            />
            <DropdownField
              name="currency"
              label="Currency"
              required
              editing={editing}
              ref={(instance) => { this.fields.currency = instance; }}
              value={record.Currency}
              possibleValues={staticData.currencies}
              mapFn={toUI.currency}
              onChange={updatedValue => onUpdateProperty('Currency', updatedValue)}
              validationMessage={'Please select a Currency'}
              toolTip="Main currency"
            />
            <DropdownField
              name="currencyPair"
              label="Currency Pair"
              editing={editing}
              ref={(instance) => { this.fields.currencyPair = instance; }}
              value={record.CurrencyPair}
              possibleValues={staticData.currencyPairs}
              mapFn={toUI.currencyPair}
              onChange={updatedValue => onUpdateProperty('CurrencyPair', updatedValue)}
              toolTip="Currency pair if instrument is FX"
            />
          </div>
          <DateTimeField
            name="order-taken"
            label="Order Taken"
            required
            ref={(instance) => { this.fields.orderTakenDate = instance; }}
            editing={editing}
            onChange={updatedValue => onUpdateProperty('OrderTakenDate', updatedValue)}
            seconds
            timeZones={staticData.timeZones}
            value={record.OrderTakenDate}
            onSetCurrentTime={timeZoneName => onSetCurrentTime('OrderTakenDate', timeZoneName)}
            clipboardDate={clipboardDate}
            onCopy={onCopyDate}
            toolTip="Timestamp of order taken"
          />

          <DateTimeField
            name="order-executed"
            label="Order Executed"
            ref={(instance) => { this.fields.orderExecutedDate = instance; }}
            editing={editing}
            onChange={updatedValue => onUpdateProperty('ExecutionDate', updatedValue)}
            seconds
            timeZones={staticData.timeZones}
            value={record.ExecutionDate}
            onSetCurrentTime={timeZoneName => onSetCurrentTime('ExecutionDate', timeZoneName)}
            clipboardDate={clipboardDate}
            onCopy={onCopyDate}
            toolTip="Timestamp of order executed"
          />
          {record.ExecutionStatus === 'Cancelled' &&
            <div className="FieldsContainer-group">
              <TextField
                name="cancellation_details"
                label="Cancelled At (UTC)"
                ref={(instance) => { this.fields.cancelledAtUTC = instance; }}
                value={record.CancelledAtUTC}
              />

              <TextField
                name="cancellation_details"
                label="Cancelled By"
                ref={(instance) => { this.fields.cancelledBy = instance; }}
                value={record.CancelledBy.Name}
              />
            </div>
          }
          <TextField
            name="order-instructions"
            label="Order Instructions"
            ref={(instance) => { this.fields.orderInstructions = instance; }}
            editing={editing}
            value={record.OrderInstructions}
            multiLine
            onChange={updatedValue => onUpdateProperty('OrderInstructions', updatedValue)}
            toolTip="Specific instructions relating to the order"
          />

          <div className="FieldsContainer-group">
            <DropdownField
              name="sales-person"
              label="Sales Person"
              required
              ref={(instance) => { this.fields.salesPerson = instance; }}
              editing={editing}
              value={record.SalesPerson}
              recentlyUsed={recentlyUsed.SalesPerson}
              possibleValues={staticData.salesPeople}
              mapFn={toUI.salesPerson}
              onChange={updatedValue => onUpdateProperty('SalesPerson', updatedValue)}
              toolTip="Sales person entering trade"
            />

            <DropdownField
              name="receiving-trader"
              label="Receiving Trader"
              required
              editing={editing}
              ref={(instance) => { this.fields.trader = instance; }}
              value={record.ReceivingTrader}
              recentlyUsed={recentlyUsed.ReceivingTrader}
              possibleValues={staticData.traders}
              mapFn={toUI.trader}
              onChange={updatedValue => onUpdateProperty('ReceivingTrader', updatedValue)}
              validationMessage="Please select a Trader"
              toolTip="Trader taking order"
            />
          </div>

          <div className="FieldsContainer-group">
            <DropdownField
              name="front-office-system"
              label="Front Office System"
              editing={editing}
              required={isInExecutedState(record)}
              value={record.FrontOfficeSystem}
              possibleValues={staticData.tradingSystems}
              mapFn={toUI.tradingSystem}
              onChange={updatedValue => onUpdateProperty('FrontOfficeSystem', updatedValue)}
              validationMessage={'Please select a FrontOffice System'}
              toolTip="Trading system"
            />

            <TextArrayField
              name="ticket-references"
              label="Ticket References"
              ref={(instance) => { this.fields.ticketReferences = instance; }}
              editing={editing}
              value={record.TicketReferences}
              onChange={updatedValue => onUpdateProperty('TicketReferences', updatedValue)}
              required={isInExecutedState(record)}
              validationMessage={'Please specify one or more Ticket References'}
              toolTip="Trade ID of FO System"
            />
          </div>
        </FieldsContainer>
      </div>
    );
  }
}


export default OrderDetailComponent;



// WEBPACK FOOTER //
// ./src/recordDetail/orderDetail/OrderDetail.js